/**
 * Settings -> Rewards activity: read-only ledger of issued rewards.
 *
 * The trust surface for the rewards feature - shows what was issued, to whom,
 * for how much, and whether it ordered / delivered / redeemed / failed.
 *
 * Mirrors the Claims list UX: status pills (basic filter) on the left, search +
 * date filter + CSV export pinned to the right. Searchable by claim id
 * (uuid / CLM-XXXXXXXX / bare prefix), consumer name, and consumer email. Server-
 * paginated via GET /integrations/rewards/issuances/. Gated by the `rewards` flag.
 */

import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { StatusPills, type StatusPillItem } from '@/admin/components/shared/status-pills'
import {
  TimeFilter,
  type TimeRange,
} from '@/admin/components/shared/time-filter'
import { timeRangeToParams } from '@/admin/lib/time-range-params'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { SearchInput } from '@/shared/components/ui/search-input'
import { ChevronDown, ChevronRight, Download, Loader2 } from 'lucide-react'
import { defineField, formatDisplayValue } from '@/shared/lib/forms'
import { useRewardIssuances, useRewardsBalance } from '@/admin/hooks/use-integrations'
import { useTableExport } from '@/admin/hooks/use-table-export'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { formatClaimId } from '@/admin/lib/formatting'
import type {
  RewardIssuanceRow,
  RewardIssuanceStatus,
} from '@/admin/types/integrations'
import { CancelRewardDialog } from './cancel-reward-dialog'

const STATUS_VARIANT: Record<
  RewardIssuanceStatus,
  'default' | 'secondary' | 'destructive' | 'outline' | 'ghost'
> = {
  pending: 'outline',
  ordered: 'secondary',
  delivered: 'default',
  redeemed: 'default',
  failed: 'destructive',
  cancelled: 'ghost',
}

const STATUS_LABELS: { key: RewardIssuanceStatus; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'ordered', label: 'Ordered' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'redeemed', label: 'Redeemed' },
  { key: 'failed', label: 'Failed' },
  { key: 'cancelled', label: 'Cancelled' },
]

const dateField = (code: string, label: string) =>
  defineField({ code_name: code, display_name: label, field_type: 'DATE_TIME' })

export function RewardsIssuancesTab() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [status, setStatus] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [timeRange, setTimeRange] = useState<TimeRange>('all_time')
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | undefined>()
  const [cancelTarget, setCancelTarget] = useState<RewardIssuanceRow | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggleExpanded = (uuid: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })

  const { t } = useTranslation()
  const { can } = usePermissions()
  const canCancel = can('rewards.cancel')

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(handle)
  }, [searchInput])

  const dateParams = useMemo(
    () => timeRangeToParams(timeRange, customRange),
    [timeRange, customRange],
  )

  const params = useMemo(
    () => ({
      page,
      page_size: pageSize,
      ...dateParams,
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
    }),
    [page, pageSize, dateParams, search, status],
  )

  const { data, isLoading } = useRewardIssuances(params)
  const { data: balance } = useRewardsBalance()

  const statusCounts = data?.status_counts ?? {}
  const totalCount = Object.values(statusCounts).reduce((n, c) => n + c, 0)
  const statusPills: StatusPillItem[] = STATUS_LABELS.filter(
    (s) => statusCounts[s.key],
  ).map((s) => ({ key: s.key, label: s.label, count: statusCounts[s.key] }))

  const columns: DataTableColumn<RewardIssuanceRow>[] = useMemo(
    () => [
      {
        id: 'expand',
        header: '',
        cell: (row) => (
          <button
            type="button"
            aria-label={
              expanded.has(row.uuid)
                ? t('rewardsActivity.hideDetails', 'Hide details')
                : t('rewardsActivity.showDetails', 'Show details')
            }
            onClick={(e) => {
              e.stopPropagation()
              toggleExpanded(row.uuid)
            }}
            className="flex h-5 w-5 items-center justify-center text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            {expanded.has(row.uuid) ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ),
      },
      {
        id: 'claim',
        header: 'Claim ID',
        cell: (row) => {
          const ref =
            row.claim_reference ||
            (row.resource_type === 'claim' ? formatClaimId(row.resource_id) : '')
          return (
            <span className="truncate font-mono text-[12px] uppercase text-[var(--ink-3)]">
              {ref || '-'}
            </span>
          )
        },
      },
      {
        id: 'recipient_name',
        header: 'Recipient',
        cell: (row) => (
          <span className="block truncate text-[13px] text-[var(--ink)]">
            {row.recipient_name || row.recipient_email || '-'}
          </span>
        ),
      },
      {
        id: 'recipient_email',
        header: 'Email',
        cell: (row) => (
          <span className="block truncate text-[13px] text-[var(--ink-2)]">
            {row.recipient_email || '-'}
          </span>
        ),
      },
      {
        id: 'amount',
        header: 'Amount',
        cell: (row) => `${row.amount} ${(row.currency || '').toUpperCase()}`.trim(),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => (
          <Badge variant={STATUS_VARIANT[row.status] ?? 'outline'}>{row.status}</Badge>
        ),
      },
      {
        id: 'created_at',
        header: 'Created',
        getValue: (row) => row.created_at,
        field: dateField('created_at', 'Created'),
        sortable: true,
        sortField: 'created_at',
      },
      {
        id: 'actions',
        header: '',
        cell: (row) =>
          canCancel && row.is_cancellable ? (
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setCancelTarget(row)}
              className="text-[var(--status-error)]"
            >
              Cancel
            </Button>
          ) : (
            <span className="text-[12px] text-[var(--ink-3)]">-</span>
          ),
      },
    ],
    [canCancel, expanded, t],
  )

  // Expanded per-row detail: the Tango cross-verification fields (order id,
  // line-item id, utid, ref, lifecycle timestamps, error). iWarranty has no
  // Tango access, so surfacing these lets the tenant reconcile in their portal.
  const renderRewardDetail = (row: RewardIssuanceRow): ReactNode => {
    if (!expanded.has(row.uuid)) return null
    const fmtDate = (v: string | null, label: string): ReactNode =>
      v ? formatDisplayValue(dateField('d', label), v) : '-'
    const items: { label: string; value: ReactNode; mono?: boolean }[] = [
      {
        label: t('rewardsActivity.orderId', 'Tango order ID'),
        value: row.provider_order_id || '-',
        mono: true,
      },
      {
        label: t('rewardsActivity.lineItemId', 'Line item ID'),
        value: row.provider_line_item_id || '-',
        mono: true,
      },
      {
        label: t('rewardsActivity.utid', 'Reward item (utid)'),
        value: row.utid || '-',
        mono: true,
      },
      { label: t('rewardsActivity.rewardRule', 'Reward rule'), value: row.reward_rule || '-' },
      {
        label: t('rewardsActivity.ordered', 'Ordered'),
        value: fmtDate(row.ordered_at, t('rewardsActivity.ordered', 'Ordered')),
      },
      {
        label: t('rewardsActivity.delivered', 'Delivered'),
        value: fmtDate(row.delivered_at, t('rewardsActivity.delivered', 'Delivered')),
      },
      {
        label: t('rewardsActivity.redeemed', 'Redeemed'),
        value: fmtDate(row.redeemed_at, t('rewardsActivity.redeemed', 'Redeemed')),
      },
      {
        label: t('rewardsActivity.cancelled', 'Cancelled'),
        value: fmtDate(row.cancelled_at, t('rewardsActivity.cancelled', 'Cancelled')),
      },
      { label: t('rewardsActivity.attempts', 'Attempts'), value: String(row.attempts) },
    ]
    return (
      // Pin the detail to the left edge of the (horizontally scrollable) table
      // and cap its width to the viewport, so it stays readable without
      // scrolling the wide table sideways.
      <div className="sticky left-0 max-w-[calc(100vw-6rem)] space-y-3 px-4 py-3">
        <p className="text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
          {t('rewardsActivity.tangoDetails', 'Tango payment details')}
        </p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-3">
          {items.map((it) => (
            <div key={it.label} className="min-w-0">
              <div className="text-[11px] text-[var(--ink-3)]">{it.label}</div>
              <div
                className={`truncate text-[12px] text-[var(--ink)] ${it.mono ? 'font-mono' : ''}`}
              >
                {it.value}
              </div>
            </div>
          ))}
        </div>
        {row.last_error_message && (
          <div className="rounded-[4px] border-[0.5px] border-solid border-[var(--status-error)] px-2.5 py-1.5">
            <div className="text-[11px] text-[var(--ink-3)]">
              {t('rewardsActivity.errorCode', 'Error {{code}}', {
                code: row.last_error_code,
              })}
            </div>
            <div className="text-[12px] text-[var(--status-error)]">
              {row.last_error_message}
            </div>
          </div>
        )}
        <p className="text-[11px] text-[var(--ink-3)]">
          {t(
            'rewardsActivity.crossCheck',
            'Cross-check the Tango order ID above in your Tango portal.',
          )}
        </p>
      </div>
    )
  }

  const { exportCsv, isExporting } = useTableExport({
    endpoint: '/integrations/rewards/issuances/',
    columns,
    columnVisibility: {},
    params: {
      ...dateParams,
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
    },
  })

  return (
    <div className="space-y-6">
      <header>
        <div className="flex items-baseline gap-3">
          <h1 className="font-display text-[24px] text-[var(--ink)]">Rewards activity</h1>
          <TimeFilter
            value={timeRange}
            customRange={customRange}
            onChange={(range, custom) => {
              setTimeRange(range)
              setCustomRange(custom)
              setPage(1)
            }}
          />
        </div>
        <p className="text-[13px] text-[var(--ink-3)]">
          Every reward issued from a claim - recipient, amount, and delivery status.
        </p>
        {balance?.available && balance.current_balance != null && (
          <p className="mt-1 text-[12px] text-[var(--ink-2)]">
            Account balance:{' '}
            <span className="text-[var(--ink)]">
              {balance.current_balance.toLocaleString()}{' '}
              {(balance.currency || '').toUpperCase()}
            </span>
          </p>
        )}
      </header>

      <div className="cv-data-table grid grid-cols-1 items-start gap-2 md:grid-cols-[minmax(0,1fr)_auto] md:gap-3">
        {/* Left: status pills (basic filter) */}
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <StatusPills
            items={statusPills}
            activeKey={status}
            onSelect={(key) => {
              setStatus(key)
              setPage(1)
            }}
            totalCount={totalCount}
          />
        </div>
        {/* Right: search + export, pinned top-right */}
        <div className="flex shrink-0 items-center gap-2 md:justify-self-end">
          <SearchInput
            placeholder="Search by claim, name, email..."
            value={searchInput}
            onChange={setSearchInput}
            iconSize={12}
            className="cv-data-table-body w-[160px] rounded-[2px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-[var(--bg)] py-[5px] pr-2.5 text-[var(--ink)] outline-none transition-[border-color] duration-150 focus:border-[var(--green)] md:w-[240px]"
          />
          <Button
            type="button"
            variant="outline"
            onClick={exportCsv}
            disabled={isExporting}
            className="h-[28px] w-[30px] shrink-0 cursor-pointer rounded-[4px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-transparent p-0 text-[var(--ink-3)] transition-all duration-150 hover:border-[var(--rule-heavy)] hover:bg-transparent hover:text-[var(--ink-2)]"
            title="Download CSV"
          >
            {isExporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>

      <DataTable<RewardIssuanceRow>
        tableId="rewards-issuances"
        columns={columns}
        data={data?.results ?? []}
        totalCount={data?.count}
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
        rowKey="uuid"
        emptyMessage="No rewards issued yet."
        renderRowDetail={renderRewardDetail}
        highlightExpandedDetail
      />

      {cancelTarget && (
        <CancelRewardDialog
          open={cancelTarget != null}
          issuance={cancelTarget}
          onClose={() => setCancelTarget(null)}
        />
      )}
    </div>
  )
}
